import { ACTION_CAPABILITY_LIST } from "./registry";
import type { ActionCapability, RuntimeActionGrounding } from "./types";

/** Runtime projection generated from the canonical ActionCapability registry. */
export const RUNTIME_ACTION_GROUNDING: Readonly<
  Record<string, RuntimeActionGrounding>
> = Object.freeze(
  Object.fromEntries(
    ACTION_CAPABILITY_LIST.filter(isRuntimeGroundedCapability).map(
      (capability) => [
        capability.name,
        {
          action: capability.name,
          ...(capability.canonicalAction
            ? { canonicalAction: capability.canonicalAction }
            : {}),
          ...(capability.aliases.length > 0
            ? { aliases: capability.aliases }
            : {}),
          category: capability.category,
          semantics: capability.semantics,
          grounding: capability.grounding,
          targetSupport: capability.targetSupport,
          evidence: capability.evidence,
          generationPolicy: capability.generationPolicy,
          description: capability.description,
          consumerCatalogDigest: capability.consumerCatalogDigest!,
          consumerContract: capability.consumerContract!,
        } satisfies RuntimeActionGrounding,
      ],
    ),
  ),
);

function isRuntimeGroundedCapability(
  capability: ActionCapability,
): capability is ActionCapability & {
  generationPolicy: RuntimeActionGrounding["generationPolicy"];
} {
  return (
    capability.runtimeImplemented && capability.generationPolicy !== "blocked"
  );
}

/**
 * Ensures the registry projection contains exactly the aliases dispatched by
 * the runtime executor. Both maps use legacy alias as the key and canonical
 * action as the value.
 */
export function assertExactRuntimeAliasProjection(
  registryAliases: ReadonlyMap<string, string>,
  executorAliases: ReadonlyMap<string, string>,
): void {
  const missing = [...executorAliases]
    .filter(([alias]) => !registryAliases.has(alias))
    .map(([alias, canonicalAction]) => `${alias}=${canonicalAction}`)
    .sort();
  const extra = [...registryAliases]
    .filter(([alias]) => !executorAliases.has(alias))
    .map(([alias, canonicalAction]) => `${alias}=${canonicalAction}`)
    .sort();
  const retargeted = [...registryAliases]
    .flatMap(([alias, registryCanonicalAction]) => {
      const executorCanonicalAction = executorAliases.get(alias);
      return executorCanonicalAction !== undefined &&
        executorCanonicalAction !== registryCanonicalAction
        ? [
            `${alias} (registry=${registryCanonicalAction}, executor=${executorCanonicalAction})`,
          ]
        : [];
    })
    .sort();

  if (missing.length === 0 && extra.length === 0 && retargeted.length === 0) {
    return;
  }

  throw new Error(
    [
      "Runtime action alias projection mismatch.",
      ...(missing.length > 0 ? [`missing aliases: ${missing.join(", ")}`] : []),
      ...(extra.length > 0 ? [`extra aliases: ${extra.join(", ")}`] : []),
      ...(retargeted.length > 0
        ? [`retargeted aliases: ${retargeted.join(", ")}`]
        : []),
    ].join(" "),
  );
}

const RUNTIME_ACTION_ALIASES = new Map<string, RuntimeActionGrounding>();
for (const grounding of Object.values(RUNTIME_ACTION_GROUNDING)) {
  for (const alias of grounding.aliases ?? []) {
    RUNTIME_ACTION_ALIASES.set(alias, grounding);
  }
}

export function getRuntimeActionGrounding(
  action: string,
): RuntimeActionGrounding | undefined {
  const canonicalGrounding = Object.prototype.hasOwnProperty.call(
    RUNTIME_ACTION_GROUNDING,
    action,
  )
    ? RUNTIME_ACTION_GROUNDING[action]
    : undefined;
  return canonicalGrounding ?? RUNTIME_ACTION_ALIASES.get(action);
}
